import {useEffect, useMemo, useState} from "react";
import {Button, Input, InputGroup, Pagination, RadioTile, RadioTileGroup, Table, toaster, Tooltip, Whisper} from "rsuite";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import {
  AiOutlineCloseIcon,
  AiOutlineSearchIcon,
  FaPlusIcon,
} from "../../../models/Icons.model";
import {
  CLOCK_STATION_MODEL_OPTIONS,
  ClockStationFirmwareItem,
  ClockStationFirmwaresResponse,
  FIRMWARE_TABLE_HEIGHT,
  PAGE_SIZE,
} from "../Firmwares.model";
import {FRONT_FILTER_PAGE_SIZE, FirmwaresUtils} from "../Firmwares.util";
import NotificationFactory from "../../../shared/Notifications";
import ClockStationFirmwareModal from "./ClockStationFirmwareModal";
import FirmwareDescriptionCell from "./FirmwareDescriptionCell";

const {Column, HeaderCell, Cell} = Table;

const ClockStationFirmwares = () => {
  const [loading, setLoading] = useState(false);
  const [firmwares, setFirmwares] = useState<ClockStationFirmwareItem[]>([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedFirmware, setSelectedFirmware] = useState<ClockStationFirmwareItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [modelFilter, setModelFilter] = useState<string | null>(null);

  const loadFirmwares = async () => {
    setLoading(true);

    try {
      const response = await ArtifactsApi.clockStationFirmwares.getList(1, FRONT_FILTER_PAGE_SIZE) as ClockStationFirmwaresResponse;
      setFirmwares(FirmwaresUtils.getItems<ClockStationFirmwareItem>(response));
    } catch (error) {
      console.error("Ошибка получения прошивок часовой станции:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getListErrorMessage(error, "прошивок часовой станции")), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFirmwares().then();
  }, []);

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(1);
  };

  const handleModelFilterChange = (value: string | null) => {
    setModelFilter(value);
    setPage(1);
  };

  const filteredFirmwares = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return firmwares.filter((firmware) => {
      const matchesModel = !modelFilter || firmware.model === modelFilter;
      const matchesSearch = !normalizedSearch || FirmwaresUtils.getSearchString(firmware).toLowerCase().includes(normalizedSearch);

      return matchesModel && matchesSearch;
    });
  }, [firmwares, searchValue, modelFilter]);

  const paginatedFirmwares = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredFirmwares.slice(start, start + PAGE_SIZE);
  }, [filteredFirmwares, page]);

  const handleOpenCreateModal = () => {
    setSelectedFirmware(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEditModal = (firmware: ClockStationFirmwareItem) => {
    setSelectedFirmware(firmware);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFirmware(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    loadFirmwares().then();
  };

  return (
    <>
      <div className="row mt-3 align-items-center firmwares-toolbar">
        <div className="col-md-1 col-sm-2 mb-3">
          <Whisper placement="top" speaker={<Tooltip>Добавить новую прошивку часовой станции</Tooltip>}>
            <Button
              style={{height: 35}}
              size="md"
              appearance="primary"
              color="green"
              onClick={handleOpenCreateModal}
            >
              <FaPlusIcon />
            </Button>
          </Whisper>
        </div>

        <div className="col-md-4 col-sm-10 mb-3">
          <InputGroup>
            <InputGroup.Addon>
              <AiOutlineSearchIcon />
            </InputGroup.Addon>
            <Input
              placeholder="Поиск прошивок часовой станции..."
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                setPage(1);
              }}
            />
            {searchValue && (
              <InputGroup.Button onClick={handleClearSearch}>
                <AiOutlineCloseIcon />
              </InputGroup.Button>
            )}
          </InputGroup>
        </div>
      </div>

      <div className="mb-3">
        <RadioTileGroup
          as="div"
          aria-label="Фильтр по модели часовой станции"
          className="w-100 firmwares-clockstation-filter-group"
          value={modelFilter || undefined}
          onChange={(value) => handleModelFilterChange(value as string)}
        >
          {CLOCK_STATION_MODEL_OPTIONS.map((option) => (
            <RadioTile
              key={option.value}
              value={option.value}
              label={option.label}
            >
              <span className="radio-tile-text">{option.label}</span>
            </RadioTile>
          ))}
        </RadioTileGroup>
      </div>

      <Table
        height={FIRMWARE_TABLE_HEIGHT}
        data={paginatedFirmwares}
        loading={loading}
        onRowClick={(rowData: ClockStationFirmwareItem) => handleOpenEditModal(rowData)}
        className="users-table firmwares-table"
        rowHeight={60}
        wordWrap
      >
        <Column width={100} sortable verticalAlign="middle" fullText>
          <HeaderCell>Версия</HeaderCell>
          <Cell dataKey="version">
            {(rowData: ClockStationFirmwareItem) => rowData.version || "-"}
          </Cell>
        </Column>

        <Column width={170} sortable verticalAlign="middle" fullText>
          <HeaderCell>Модель</HeaderCell>
          <Cell dataKey="model">
            {(rowData: ClockStationFirmwareItem) => FirmwaresUtils.formatClockStationModel(rowData.model)}
          </Cell>
        </Column>

        <Column flexGrow={1} sortable verticalAlign="middle">
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description">
            {(rowData: ClockStationFirmwareItem) => <FirmwareDescriptionCell description={rowData.description} />}
          </Cell>
        </Column>

        <Column flexGrow={1} verticalAlign="middle" fullText>
          <HeaderCell>Файл</HeaderCell>
          <Cell>
            {(rowData: ClockStationFirmwareItem) => rowData.fileDescriptor?.name || "-"}
          </Cell>
        </Column>

        <Column width={110} verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Размер</HeaderCell>
          <Cell>
            {(rowData: ClockStationFirmwareItem) => FirmwaresUtils.formatFileSize(rowData.fileDescriptor?.size)}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" fullText>
          <HeaderCell>Создал</HeaderCell>
          <Cell dataKey="createdBy">
            {(rowData: ClockStationFirmwareItem) => rowData.createdBy || "-"}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Создано</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: ClockStationFirmwareItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>

      <div className="d-flex justify-content-end mt-3">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={["total", "-", "pager"]}
          total={filteredFirmwares.length}
          limit={PAGE_SIZE}
          activePage={page}
          onChangePage={setPage}
        />
      </div>

      <ClockStationFirmwareModal
        open={modalOpen}
        mode={modalMode}
        firmware={selectedFirmware}
        onClose={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default ClockStationFirmwares;
